import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getRedemptions, getCurrentMember, isAuthenticated, Redemption, Member } from '../services/api';
import { Ticket, Gift, Clock, CheckCircle, XCircle, AlertCircle, Trophy, Medal, Award, Calendar } from 'lucide-react';

const RedemptionsPage: React.FC = () => {
  const navigate = useNavigate();
  const [redemptions, setRedemptions] = useState<Redemption[]>([]);
  const [member, setMember] = useState<Member | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    try {
      const [redemptionsData, memberData] = await Promise.all([
        getRedemptions(),
        getCurrentMember()
      ]);
      setRedemptions(redemptionsData.redemptions);
      setMember(memberData);
    } catch (err) {
      console.error('Failed to load redemptions:', err);
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'Completed': return CheckCircle;
      case 'Cancelled': return XCircle;
      case 'Pending': return Clock;
      default: return AlertCircle;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'Completed': return 'text-green-600 bg-green-100';
      case 'Cancelled': return 'text-red-600 bg-red-100';
      case 'Pending': return 'text-yellow-600 bg-yellow-100';
      case 'Confirmed': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getTierIcon = (tier: string) => {
    switch (tier) {
      case 'Gold': return Trophy;
      case 'Silver': return Medal;
      default: return Award;
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'Gold': return 'text-yellow-500';
      case 'Silver': return 'text-gray-400';
      default: return 'text-amber-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-buggy-blue mx-auto mb-4"></div>
          <p className="text-buggy-gray">Loading redemptions...</p>
        </div>
      </div>
    );
  }

  const TierIcon = member ? getTierIcon(member.currentTier) : Award;
  const tierColor = member ? getTierColor(member.currentTier) : 'text-amber-700';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Sticky Header */}
      <div className="sticky top-0 z-40">
        <div className="bg-buggy-blue text-white p-6">
          <div className="max-w-md mx-auto">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold">My Redemptions</h1>
                <p className="text-sm opacity-90">Your reward history</p>
              </div>
              {member && (
                <div className="flex flex-col items-end">
                  <div className="inline-flex items-center gap-1.5 bg-white rounded-full px-3 py-1.5 mb-2 shadow-sm">
                    <TierIcon size={16} strokeWidth={2.5} className={tierColor} />
                    <span className="text-xs font-semibold text-buggy-dark">{member.currentTier} Tier</span>
                  </div>
                  <p className="text-xs opacity-75">Your Points</p>
                  <p className="text-2xl font-bold">{member.currentPoints}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto pb-20">
        <div className="max-w-md mx-auto p-4">
        {redemptions.length === 0 ? (
          /* Empty State */
          <div className="bg-white rounded-xl shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-buggy-blue/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <Gift size={40} strokeWidth={2} className="text-buggy-blue" />
            </div>
            <h2 className="text-xl font-bold text-buggy-dark mb-2">No Redemptions Yet</h2>
            <p className="text-buggy-gray mb-6">
              You haven't redeemed any rewards yet. Start exploring your exclusive perks!
            </p>
            <button
              onClick={() => navigate('/rewards')}
              className="bg-buggy-blue text-white font-semibold py-3 px-6 rounded-lg hover:bg-buggy-blue-light transition-colors"
            >
              Browse Rewards
            </button>
          </div>
        ) : (
          /* Redemptions List */
          <div className="space-y-4">
            {redemptions.map((redemption) => {
              const StatusIcon = getStatusIcon(redemption.redemptionStatus);
              const statusColor = getStatusColor(redemption.redemptionStatus);

              return (
                <div
                  key={redemption.redemptionId}
                  className="bg-white rounded-xl shadow-md p-5"
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 bg-buggy-blue/10 rounded-lg flex items-center justify-center">
                        <Ticket size={24} strokeWidth={2} className="text-buggy-blue" />
                      </div>
                      <div>
                        <h3 className="font-bold text-buggy-dark">{redemption.rewardName}</h3>
                        <p className="text-xs text-buggy-gray">{redemption.rewardCategory}</p>
                      </div>
                    </div>
                    <span className={`text-xs px-2 py-1 rounded-full flex items-center gap-1 ${statusColor}`}>
                      <StatusIcon size={12} strokeWidth={2} />
                      {redemption.redemptionStatus}
                    </span>
                  </div>

                  {/* Redemption Code */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-3">
                    <p className="text-xs text-buggy-gray mb-1">Redemption Code</p>
                    <p className="text-xl font-bold text-buggy-blue tracking-wider">
                      {redemption.redemptionCode}
                    </p>
                  </div>

                  {/* Details */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div className="flex items-center gap-2 text-buggy-gray">
                      <Calendar size={14} strokeWidth={2} />
                      <span>{new Date(redemption.createdDate).toLocaleDateString()}</span>
                    </div>
                    {redemption.pointsRedeemed > 0 && (
                      <div className="flex items-center gap-2 text-buggy-gray">
                        <Gift size={14} strokeWidth={2} />
                        <span>{redemption.pointsRedeemed} pts</span>
                      </div>
                    )}
                  </div>

                  {/* Scheduled Date */}
                  {redemption.confirmedDate && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <p className="text-sm text-buggy-dark">
                        <span className="font-semibold">Scheduled:</span>{' '}
                        {new Date(redemption.confirmedDate).toLocaleDateString('en-US', {
                          weekday: 'long',
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  )}

                  {/* Instructions */}
                  {redemption.instructions && redemption.redemptionStatus !== 'Completed' && redemption.redemptionStatus !== 'Cancelled' && (
                    <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                      <p className="text-xs text-blue-700">{redemption.instructions}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
        </div>
      </div>
    </div>
  );
};

export default RedemptionsPage;
